import * as turf from "@turf/turf"
import type { Feature, FeatureCollection, Point, Polygon } from "geojson"
import { memo, useCallback, useMemo, useState } from "react"

import {
	CIRCLE_POLYGON_STEPS,
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler } from "../typings"
import { Layer, Source } from "./memo"

const CIRCLE_DRAWER_SOURCE_ID = "circle-drawer-source"

enum CircleDrawerLayerIds {
	CIRCLE = "circle-drawer-circle-layer",
	LINE = "circle-drawer-line-layer",
	FILL = "circle-drawer-fill-layer",
}

interface CircleDrawerProps {
	onComplete?: (circle: Polygon) => void
}

export const CircleDrawer = memo<CircleDrawerProps>((props) => {
	const { onComplete } = props

	const [firstPoint, setFirstPoint] = useState<Feature<Point> | null>(null)
	const [secondPoint, setSecondPoint] = useState<Feature<Point> | null>(null)

	const reset = useCallback(() => {
		setFirstPoint(null)
		setSecondPoint(null)
	}, [])

	const handleMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint || !secondPoint) {
				setFirstPoint(point)
				setSecondPoint(point)
			} else {
				onComplete?.(
					turf.circle(firstPoint, turf.distance(firstPoint, secondPoint), { steps: CIRCLE_POLYGON_STEPS })
						.geometry,
				)
				reset()
			}
		},
		[firstPoint, onComplete, reset, secondPoint],
	)

	const handleMouseMove = useCallback<MapEventHandler<"mousemove">>(
		(e) => {
			const { lngLat } = e

			if (!firstPoint || !secondPoint) return

			const point = turf.point(lngLat.toArray())
			setSecondPoint(point)
		},
		[firstPoint, secondPoint],
	)

	useCurrentMapEvent("click", handleMapClick)
	useCurrentMapEvent("mousemove", handleMouseMove)

	const geojson = useMemo<FeatureCollection>(() => {
		const features: Feature[] = []

		if (firstPoint && secondPoint) {
			features.push(firstPoint, secondPoint)
			const circle = turf.circle(firstPoint, turf.distance(firstPoint, secondPoint), {
				steps: CIRCLE_POLYGON_STEPS,
			})
			features.push(circle)
			// @ts-expect-error REASON: sector is a singular Polygon feature
			features.push(turf.polygonToLine(circle))
		}

		return turf.featureCollection(features)
	}, [firstPoint, secondPoint])

	return (
		<Source id={CIRCLE_DRAWER_SOURCE_ID} type="geojson" data={geojson}>
			<Layer
				id={CircleDrawerLayerIds.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={CircleDrawerLayerIds.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={CircleDrawerLayerIds.CIRCLE}
			/>
			<Layer
				id={CircleDrawerLayerIds.FILL}
				type="fill"
				paint={DRAW_FILL_LAYER_PAINT}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
				beforeId={CircleDrawerLayerIds.LINE}
			/>
		</Source>
	)
})
CircleDrawer.displayName = "CircleDrawer"
